import { useState } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import { useHistory } from "react-router-dom";

import MessageTip from "../../MessageTip";

import firebaseService from "../../../../services/firebase";
import { transformPhoneNumber } from "../utilities";
import { STATUS } from "../../../../constants";
import { isPhoneInputDisabled } from "./utilities";
import useTimer from "../../useTimer";
import useRecaptcha from "../../useRecaptcha";
import RecaptchaLoadTip from "../../RecaptchaLoadTip";
import { isRecaptchaCredentialError } from "../../recaptchaUtils";
import { reportRecaptchaAuthError } from "../../../../infra/sentry";
import { setMfaSession } from "../../../../utils/mfaSession";

const { SUCCEED, FAILED, LOADING, DEFAULT } = STATUS;

const PhoneVerification = () => {
  const history = useHistory();
  const [smsSentStatus, setSmsSentStatus] = useState({
    status: DEFAULT,
  });
  const [emailVerifiedResult, setEmailVerifiedResult] = useState({
    status: DEFAULT,
  });
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [verificationCode, setVerificationCode] = useState(null);
  const [verificationId, setVerificationId] = useState(false);
  const [sendBtnDisabled, setSendBtnDisabled] = useState(false);
  const { startTimer, countdown } = useTimer();
  const currentUser = firebaseService.currentUser();
  const {
    recaptchaVerifier,
    isRecaptchaReady,
    isRecaptchaLoading,
    hasRecaptchaLoadFailed,
    resetRecaptcha,
    resetRecaptchaWidget,
  } = useRecaptcha({
    containerId: "recaptcha-container",
  });

  const handleSendSMS = async () => {
    if (smsSentStatus.status === LOADING || !isRecaptchaReady || !recaptchaVerifier) {
      return;
    }

    setSmsSentStatus({
      status: LOADING,
    });

    const transformedPhoneNumber = transformPhoneNumber(phoneNumber);
    if (!transformedPhoneNumber.isValid) {
      setSmsSentStatus({
        status: FAILED,
        error: transformedPhoneNumber.error,
      });
      return;
    }

    try {
      const multiFactorSession = await currentUser.multiFactor.getSession();
      const phoneInfoOptions = {
        phoneNumber: transformedPhoneNumber.formattedValue,
        session: multiFactorSession,
      };
      const phoneAuthProvider = new firebase.auth.PhoneAuthProvider();

      const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);
      setVerificationId(verificationId);
      setSmsSentStatus({
        status: SUCCEED,
      });
      setSendBtnDisabled(true);
      startTimer({ second: 20, onTimeout: () => setSendBtnDisabled(false) });
    } catch (error) {
      reportRecaptchaAuthError(error, { phase: "send-sms", containerId: "recaptcha-container", flow: "mfa-enroll" });
      setVerificationId(null);
      if (isRecaptchaCredentialError(error)) {
        setSmsSentStatus({
          status: FAILED,
          error: "Your session or security check expired. We have reset it — please try sending the code again.",
        });
        await resetRecaptchaWidget();
        return;
      }
      setSmsSentStatus({
        status: FAILED,
        error: "failed to send verification code",
      });
      await resetRecaptcha();
    }
  };

  const handleEnableMFA = async () => {
    if (emailVerifiedResult.status === LOADING) {
      return;
    }

    if (!verificationCode || !verificationId) {
      setEmailVerifiedResult({
        status: FAILED,
        error: "Verification Code is not empty",
      });
      return;
    }

    setEmailVerifiedResult({
      status: LOADING,
    });

    try {
      const cred = firebase.auth.PhoneAuthProvider.credential(verificationId, verificationCode);
      const multiFactorAssertion = firebase.auth.PhoneMultiFactorGenerator.assertion(cred);
      await currentUser.multiFactor.enroll(multiFactorAssertion);
      // Enrolling completes the sign in, so record the verification here too -
      // App validates the session as soon as it mounts on the next line.
      setMfaSession({
        sessionId: verificationId,
        loginEmail: currentUser.email,
        firebaseAuthUid: currentUser.uid,
      });
      setEmailVerifiedResult({
        status: SUCCEED,
      });
      history.push("/");
    } catch (error) {
      reportRecaptchaAuthError(error, { phase: "verify-code", flow: "mfa-enroll" });
      if (error.code === "auth/invalid-multi-factor-session") {
        setEmailVerifiedResult({
          status: FAILED,
          error: `Session expired, please refresh your browser.`,
        });
      } else if (error.code === "auth/too-many-requests") {
        setEmailVerifiedResult({
          status: FAILED,
          error: `Sorry, too many attempts, please try again later.`,
        });
      } else if (isRecaptchaCredentialError(error)) {
        setEmailVerifiedResult({
          status: FAILED,
          error: "Your verification code expired. Please send a new code and try again.",
        });
        await resetRecaptchaWidget();
      } else {
        setEmailVerifiedResult({
          status: FAILED,
          error: "Failed to enroll MFA.",
        });
      }
    }
  };

  return (
    <form className="public-secure-file-form" onSubmit={(event) => event.preventDefault()}>
      <div className="public-secure-file-notice">
        <i className="fas fa-mobile-screen" aria-hidden="true" />
        <p>Enter your Australian mobile number. We will send a verification code via SMS.</p>
      </div>
      <p className="public-secure-file-document-label">Step 2: Verify your phone number</p>
      <label className="public-secure-file-label" htmlFor="mfa-enroll-phone">
        Phone number (+61)
      </label>
      <input
        id="mfa-enroll-phone"
        onChange={(event) => {
          if (isPhoneInputDisabled(smsSentStatus.status)) {
            return;
          }
          setPhoneNumber(event.target.value);
        }}
        className="public-secure-file-input"
        type="tel"
        inputMode="numeric"
        disabled={isPhoneInputDisabled(smsSentStatus.status)}
        placeholder="4XXXXXXXX"
      />
      <div className="field" id="recaptcha-container"></div>
      <RecaptchaLoadTip
        isRecaptchaLoading={isRecaptchaLoading}
        hasRecaptchaLoadFailed={hasRecaptchaLoadFailed}
        onRetry={resetRecaptcha}
      />
      <button
        disabled={!isRecaptchaReady || sendBtnDisabled}
        className={`public-secure-file-button public-secure-file-button--primary ${
          smsSentStatus.status === LOADING ? "is-loading" : ""
        }`}
        type="button"
        onClick={handleSendSMS}
      >
        Send SMS
      </button>
      {countdown > 0 && <p className="is-size-7 has-text-danger">Resend in {countdown} seconds</p>}
      <MessageTip statusObj={smsSentStatus} />
      <label className="public-secure-file-label" htmlFor="mfa-enroll-code">
        Verification code
      </label>
      <input
        id="mfa-enroll-code"
        onChange={(event) => setVerificationCode(event.target.value)}
        className="public-secure-file-input"
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
      />
      {smsSentStatus.status === SUCCEED && (
        <>
          <button
            className={`public-secure-file-button public-secure-file-button--primary ${
              emailVerifiedResult.status === STATUS.LOADING ? "is-loading" : ""
            }`}
            type="button"
            onClick={handleEnableMFA}
          >
            Verify
          </button>
          <p className="is-size-7 has-text-info">
            You will be signed in once you have successfully enrolled your phone number
          </p>
        </>
      )}
      <MessageTip statusObj={emailVerifiedResult} />
    </form>
  );
};
export default PhoneVerification;
